import { Breadcrumb, Card, Divider, Drawer, Empty, List, Space, Typography } from 'antd';

import { useProgramDetail } from '@/entities/training/api/libraryApi';
import { MarkdownSection } from '@/shared/ui/MarkdownSection/MarkdownSection';
import { PageSpinner } from '@/shared/ui/PageSpinner';

import {
  buildExternalTrainingMarkdown,
  buildHowToLearnMarkdown,
  buildHowToPassMarkdown,
  buildMechanicsMarkdown,
  buildRequiredInfoMarkdown,
  buildTrainingKindMarkdown,
} from './programDrawerMarkdown';
import { libraryPathToBreadcrumbTitles } from './libraryPathBreadcrumb';
import s from './ProgramDetailDrawer.module.scss';

const { Title, Text } = Typography;

interface Props {
  programId: string | null;
  onClose: () => void;
}

export function ProgramDetailDrawer({ programId, onClose }: Props) {
  const { data: program, isLoading } = useProgramDetail(programId);

  return (
    <Drawer
      placement="right"
      title="Программа обучения"
      open={!!programId}
      onClose={onClose}
      width={640}
      destroyOnClose
      className={s.root}
    >
      {isLoading ? (
        <PageSpinner size="default" />
      ) : !program ? (
        <Empty description="Программа не найдена" />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className={s.hero}>
            <div className={s['hero-title-row']}>
              <Title level={4} className={s['hero-title']}>
                {program.name}
              </Title>
            </div>
            <Breadcrumb
              className={s['hero-breadcrumb']}
              separator="/"
              items={(() => {
                const titles = libraryPathToBreadcrumbTitles(program.library_node.path);
                const filtered = titles.filter((t) => t !== program.name);
                const crumbs = (filtered.length > 0 ? filtered : [program.library_node.name]).map((title) => ({
                  title,
                }));
                return crumbs;
              })()}
            />
          </div>

          {program.description && program.description !== program.name ? (
            <MarkdownSection content={program.description} className={s['md-section']} />
          ) : null}

          <Divider className={s['section-divider']} />

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Вид обучения
            </Title>
            <MarkdownSection content={buildTrainingKindMarkdown(program)} className={s['md-section']} />
          </section>

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Механика обучения
            </Title>
            <MarkdownSection content={buildMechanicsMarkdown(program)} className={s['md-section']} />
          </section>

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Внешнее обучение
            </Title>
            <MarkdownSection content={buildExternalTrainingMarkdown(program)} className={s['md-section']} />
          </section>

          <Divider className={s['section-divider']} />

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Как сдать
            </Title>
            <MarkdownSection content={buildHowToPassMarkdown(program)} className={s['md-section']} />
          </section>

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Как обучиться
            </Title>
            <MarkdownSection content={buildHowToLearnMarkdown(program)} className={s['md-section']} />
          </section>

          <section className={s['md-block']}>
            <Title level={5} className={s['section-title']}>
              Информация необходимая
            </Title>
            <MarkdownSection content={buildRequiredInfoMarkdown(program)} className={s['md-section']} />
          </section>

          {program.levels.length > 0 ? (
            <>
              <Divider className={s['section-divider']} />
              <Title level={5} className={s['section-title']}>
                Уровни / группы
              </Title>

              <List
                dataSource={program.levels}
                split={false}
                renderItem={(level) => (
                  <List.Item className={s['level-item']}>
                    <Card className={s['level-card']} size="small">
                      <div className={s['level-card-title']}>
                        <Text strong>{level.level_name}</Text>
                      </div>

                      <div className={s['level-card-meta']}>
                        <div className={s['level-meta']}>
                          <div className={s['level-meta-label']}>Периодичность</div>
                          <div className={s['level-meta-value']}>
                            {level.level_period_months != null ? `${level.level_period_months} мес.` : '—'}
                          </div>
                        </div>
                        <div className={s['level-meta']}>
                          <div className={s['level-meta-label']}>Срок действия</div>
                          <div className={s['level-meta-value']}>
                            {level.level_validity_months != null ? `${level.level_validity_months} мес.` : '—'}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </>
          ) : null}
        </Space>
      )}
    </Drawer>
  );
}
